import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Cart, CartBill, CartFees, PaymentCart } from '@cargos/sprintpay-models';
import { Subject, takeUntil } from 'rxjs';
import { CartBillService, DynamicsFieldsService } from 'src/app/shared/services';
import { AwbSplitByFlightService } from '../../../cart-items/awb-split-by-flight/services/awb-split-by-flight.service';
import { FeeComponent } from '../../shared/components/fee/fee.component';
import { FeesConfiguration } from '../../shared/models/convenience-fee.enum';
import { AwbItemComponent } from '../awb-item/awb-item.component';
import { ExtraItemComponent } from '../extra-item/extra-item.component';

@Component({
    selector: 'app-payment-details-rates',
    imports: [ExtraItemComponent, AwbItemComponent, CommonModule, FeeComponent],
    templateUrl: './payment-details-rates.component.html',
    styleUrl: './payment-details-rates.component.scss',
    standalone: true,
})
export class PaymentDetailsRatesComponent implements OnInit, OnChanges, OnDestroy {
    public newFees: FeesConfiguration[] = [];
    private unsubscribe$: Subject<void>;
    private cartBill: Cart | null = null;

    @Input() payment: PaymentCart;
    @Input() customerReferenceLabel: string = 'Customer reference';

    constructor(
        private awbByFlightService: AwbSplitByFlightService,
        private dynamicFieldsService: DynamicsFieldsService,
        private cartBillService: CartBillService,
        private cdr: ChangeDetectorRef
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getCustomerReferenceLabel();
        this.subscribeCartBill();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['payment'] && changes['payment'].currentValue) {
            this.loadConvenienceFees();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private loadConvenienceFees(): void {
        const defaultFees = this.getDefaultFees(this.payment);
        const serviceFees = this.awbByFlightService.getConvenienceFeesByPaymentRequest();
        const fees = serviceFees?.length ? serviceFees : defaultFees;
        this.updateFeesInformation(fees);
    }

    private getDefaultFees(payment: PaymentCart): FeesConfiguration[] {
        return [
            {
                feeName: CartFees.HANDLING_FEE,
                feeLabel: CartBill.getFeeDescription(CartFees.HANDLING_FEE_TOTAL) + ': ',
                feeValue: payment?.handlingFee || 0,
            },
            {
                feeName: CartFees.CREDIT_CARD_FEE,
                feeLabel: CartBill.getFeeDescription(CartFees.CREDIT_CARD_FEE) + ': ',
                feeValue: payment?.creditCardFee || 0,
            },
        ];
    }

    getTotalFees(feeList: FeesConfiguration[]): number {
        return feeList.reduce((sum, keyFee) => sum + (this.payment[keyFee.feeName] || 0), 0);
    }

    private updateFeesInformation(feeList: FeesConfiguration[]): void {
        const updatedFees = feeList.map((fee) => ({
            ...fee,
            feeLabel: fee.feeName === CartFees.CUSTOMS_FEE ? this.payment.customsFeeLabel || '' : fee.feeLabel,
        }));
        const convenienceFees = [...updatedFees, ...this.getDefaultFeesByRequest(feeList)];
        this.newFees = convenienceFees
            .map((fee) => ({
                ...fee,
                feeValue: fee.feeValue || this.payment[fee.feeName],
            }))
            .filter((fee) => fee.feeValue && fee.feeValue > 0);

        this.cdr.detectChanges();
    }

    getDefaultFeesByRequest(feeList: FeesConfiguration[]): FeesConfiguration[] {
        const lendingInterest = this.getFinanceFee();
        const totalFees = this.getTotalFees(feeList);
        const amount = this.payment?.amount || 0;
        const total = amount + totalFees + lendingInterest;
        const awbInfo = this.cartBillService.getAWBInfoById(this.payment.awb!, this.payment.id || 0);
        return [
            {
                feeName: 'sprintpay-flex-fee',
                feeLabel: 'SprintPay Flex Finance Fee:',
                feeValue: awbInfo?.lendingInterest || lendingInterest || 0,
            },
            {
                feeName: 'sales-tax',
                feeLabel: 'Sales Tax: ',
                feeValue: awbInfo?.taxAmountAwb || 0,
            },
            {
                feeName: 'amount',
                feeLabel: 'Amount: ',
                feeValue: awbInfo?.subtotalAwb || this.payment?.amount || 0,
            },
            { feeName: 'total', feeLabel: 'Total: ', feeValue: awbInfo?.totalAwb || total || 0 },
        ];
    }

    getCustomerReferenceLabel(): void {
        this.dynamicFieldsService
            .getFieldLabelByName('customerRef')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (fieldLabel) => {
                    this.customerReferenceLabel = fieldLabel;
                },
            });
    }

    subscribeCartBill(): void {
        this.cartBillService
            .getCartBill$()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (cartBill) => {
                    this.cartBill = cartBill;
                    this.loadConvenienceFees();
                },
            });
    }

    getFinanceFee(): number {
        const awb = this.cartBill?.cartBillResponseDTOList?.find((awb) =>
            awb.paymentRequest?.some((payment) => payment.id === this.payment.id)
        );

        if (!awb || !awb?.lendingInterest) {
            return 0;
        }

        return awb.lendingInterest;
    }
}
